import { observer } from 'mobx-react-lite'
import { ActionToggle } from '@/components/ActionToggle'
import { FormGroupContainer } from '@/components/FormContainer'
import { NumericInput } from '@/components/inputs/NumericInput'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import {
  EditorList,
  SortableEditorListItem,
} from '@/features/legends/components/EditorList'
import { TypeBadge } from '@/features/legends/components/TypeBadge'
import { TypeSelector } from '@/features/legends/questions/components/TypeSelector'
import { commitOnEnter } from '@/features/legends/editor/commit-on-enter'
import type { AnyQuestionEditor } from '@/features/legends/questions/question-registry'
import { SingleOptionSelect } from '@/features/legends/components/OptionSelect'
import {
  QuestionFieldRow,
  QuestionOptionRow,
} from '@/features/legends/questions/components/QuestionGeneralEditor'
import type { BoolQuestionEditorViewModel } from '@/features/legends/questions/types/BoolQuestionEditorViewModel'
import type { BreakerQuestionEditorViewModel } from '@/features/legends/questions/types/BreakerQuestionEditorViewModel'
import type { DateTimeQuestionEditorViewModel } from '@/features/legends/questions/types/DateTimeQuestionEditorViewModel'
import type { DocumentsQuestionEditorViewModel } from '@/features/legends/questions/types/DocumentsQuestionEditorViewModel'
import type { EffectQuestionEditorViewModel } from '@/features/legends/questions/types/EffectQuestionEditorViewModel'
import type { InventoryItemQuestionEditorViewModel } from '@/features/legends/questions/types/InventoryItemQuestionEditorViewModel'
import type { NumericQuestionEditorViewModel } from '@/features/legends/questions/types/NumericQuestionEditorViewModel'
import type { PathQuestionEditorViewModel } from '@/features/legends/questions/types/PathQuestionEditorViewModel'
import type { PersonalOrganizerCollectedValueQuestionEditorViewModel } from '@/features/legends/questions/types/PersonalOrganizerCollectedValueQuestionEditorViewModel'
import type { PersonalOrganizerPrefillQuestionEditorViewModel } from '@/features/legends/questions/types/PersonalOrganizerPrefillQuestionEditorViewModel'
import type { SelectQuestionEditorViewModel } from '@/features/legends/questions/types/SelectQuestionEditorViewModel'
import type { TextQuestionEditorViewModel } from '@/features/legends/questions/types/TextQuestionEditorViewModel'
import type { VariableQuestionEditorViewModel } from '@/features/legends/questions/types/VariableQuestionEditorViewModel'
import { FormulaFieldContainer } from '@/features/legends/ryno/components/FormulaFieldContainer'

export interface QuestionGeneralEditorContainerProps {
  /**
   * Documents questions are deprecated, and have no fields to edit.
   */
  vm: Exclude<AnyQuestionEditor, DocumentsQuestionEditorViewModel>
  isDisabled: boolean
  testId?: string
}

/**
 * The inspector's General tab: the question's own fields.
 */
export const QuestionGeneralEditorContainer = observer(
  function QuestionGeneralEditorContainer({
    vm,
    isDisabled,
    testId,
  }: QuestionGeneralEditorContainerProps) {
    switch (vm.type) {
      case 'Bool':
        return (
          <QuestionTextContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
            descriptionHelp="Some additional context to the question"
          />
        )
      case 'Text':
        return (
          <QuestionTextContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
            descriptionHelp="Give the user a hint as to what to enter."
          />
        )
      case 'Breaker':
        return (
          <QuestionTextContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
            questionLabel="Title"
            descriptionHelp="Give the user more info about why they can't continue."
          />
        )
      case 'DateTime':
        return (
          <DateTimeQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
      case 'Numeric':
        return (
          <NumericQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
      case 'Select':
        return (
          <SelectQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
      case 'Effect':
        return (
          <LabelContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
            help="Not required, but might help out your future self with understanding what this effect does."
          />
        )
      case 'Path':
        return (
          <LabelContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
            help="Optional. Not visible to the end user, just a hint at what this path is for."
          />
        )
      case 'InventoryItem':
        return (
          <InventoryItemQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
      case 'Variable':
        return (
          <VariableQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
      case 'PersonalOrganizerCollectedValue':
        return (
          <CollectedValueQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
      case 'PersonalOrganizerPrefill':
        return (
          <PrefillQuestionContainer
            vm={vm}
            isDisabled={isDisabled}
            testId={testId}
          />
        )
    }
  },
)

interface EditorContainerProps<T> {
  vm: T
  isDisabled: boolean
  testId?: string
}

type AskedQuestionViewModel =
  | BoolQuestionEditorViewModel
  | BreakerQuestionEditorViewModel
  | DateTimeQuestionEditorViewModel
  | NumericQuestionEditorViewModel
  | SelectQuestionEditorViewModel
  | TextQuestionEditorViewModel

interface QuestionTextContainerProps extends EditorContainerProps<AskedQuestionViewModel> {
  questionLabel?: string
  descriptionHelp: string
}

const QuestionTextContainer = observer(function QuestionTextContainer({
  vm,
  isDisabled,
  questionLabel = 'Question',
  descriptionHelp,
  testId,
}: QuestionTextContainerProps) {
  return (
    <>
      <TextInputContainer
        model={vm}
        field="question"
        label={questionLabel}
        error={vm.validation.getError('question')}
        isDisabled={isDisabled}
        data-test={testId && `${testId}-question-input`}
      />
      <TextAreaContainer
        model={vm}
        field="description"
        label="Description"
        help={descriptionHelp}
        isDisabled={isDisabled}
        autoSize
        minRows={5}
        data-test={testId && `${testId}-description-input`}
      />
    </>
  )
})

const DateTimeQuestionContainer = observer(function DateTimeQuestionContainer({
  vm,
  isDisabled,
  testId,
}: EditorContainerProps<DateTimeQuestionEditorViewModel>) {
  return (
    <>
      <QuestionTextContainer
        vm={vm}
        isDisabled={isDisabled}
        testId={testId}
        descriptionHelp="Give the user a hint as to what to enter."
      />
      <ActionToggle
        label="Collect time"
        description="If checked, the user will be asked for a time as well. If not, start of day is used. All dates/times are collected in the user system's time zone, regardless of this setting."
        value={vm.collectTime}
        onChange={vm.setCollectTime}
        isDisabled={isDisabled}
        testId={testId && `${testId}-collect-time-toggle`}
      />
      <FormGroupContainer error={vm.validation.getError('disallowPast')}>
        <ActionToggle
          label="Disallow past times"
          description="If checked, the user can only select dates in the future (deadlines)."
          value={vm.disallowPast}
          onChange={vm.setDisallowPast}
          isDisabled={isDisabled}
          testId={testId && `${testId}-disallow-past-toggle`}
        />
      </FormGroupContainer>
      <FormGroupContainer error={vm.validation.getError('disallowFuture')}>
        <ActionToggle
          label="Disallow future times"
          description="If checked, the user can only select dates in the past (birthdays)."
          value={vm.disallowFuture}
          onChange={vm.setDisallowFuture}
          isDisabled={isDisabled}
          testId={testId && `${testId}-disallow-future-toggle`}
        />
      </FormGroupContainer>
    </>
  )
})

const NumericQuestionContainer = observer(function NumericQuestionContainer({
  vm,
  isDisabled,
  testId,
}: EditorContainerProps<NumericQuestionEditorViewModel>) {
  return (
    <>
      <QuestionTextContainer
        vm={vm}
        isDisabled={isDisabled}
        testId={testId}
        descriptionHelp="Give the user a hint as to what to enter."
      />
      <ActionToggle
        label="Allow decimals"
        description="If checked, allows decimals."
        value={vm.isDecimal}
        onChange={vm.setDecimal}
        isDisabled={isDisabled}
        testId={testId && `${testId}-allow-decimals-toggle`}
      />
      {vm.isDecimal && (
        <FormGroupContainer
          label="Precision"
          help="How many decimals to use"
          error={vm.validation.getError('places')}
        >
          <NumericInput
            value={vm.places}
            onChange={vm.setPlaces}
            isDisabled={isDisabled}
            status={vm.validation.getError('places') ? 'danger' : 'default'}
            testId={testId && `${testId}-precision-input`}
          />
        </FormGroupContainer>
      )}
    </>
  )
})

const selectOptionGroup = Symbol('select-option')

const SelectQuestionContainer = observer(function SelectQuestionContainer({
  vm,
  isDisabled,
  testId,
}: EditorContainerProps<SelectQuestionEditorViewModel>) {
  return (
    <>
      <QuestionTextContainer
        vm={vm}
        isDisabled={isDisabled}
        testId={testId}
        descriptionHelp="Provide additional context."
      />
      <ActionToggle
        label="Multi"
        description="Can the user select more than one option?"
        value={vm.multi}
        onChange={vm.setMulti}
        isDisabled={isDisabled}
        testId={testId && `${testId}-multi-toggle`}
      />
      <FormGroupContainer
        label="Options"
        error={vm.validation.getError('options')}
      >
        <EditorList
          onAdd={vm.addOption}
          addLabel="Add option"
          isDisabled={isDisabled}
          testId={testId && `${testId}-options`}
        >
          {vm.options.map((optionVm, index) => {
            const optionTestId =
              testId && `${testId}-option-${optionVm.id || optionVm.cid}`
            return (
              <SortableEditorListItem
                key={optionVm.cid}
                group={selectOptionGroup}
                index={index}
                onMove={vm.moveByIndex}
                isDisabled={isDisabled}
                testId={optionTestId}
              >
                <QuestionOptionRow
                  onRemove={() => vm.removeOption(optionVm)}
                  removeLabel="Remove option"
                  isDisabled={isDisabled}
                  testId={optionTestId && `${optionTestId}-fields`}
                >
                  <TextInputContainer
                    model={optionVm}
                    field="text"
                    onBlur={optionVm.deriveIdFromText}
                    error={optionVm.validation.getError('text')}
                    autoFocus={optionVm === vm.addedOption}
                    placeholder="Text"
                    isDisabled={isDisabled}
                    aria-label="Option text"
                    data-test={optionTestId && `${optionTestId}-text-input`}
                  />
                  <TextInputContainer
                    model={optionVm}
                    field="idDraft"
                    onBlur={optionVm.commitId}
                    onKeyDown={commitOnEnter(optionVm.commitId)}
                    error={optionVm.validation.getError('id')}
                    placeholder="ID (used in formulas)"
                    isDisabled={isDisabled}
                    aria-label="Option ID"
                    data-test={optionTestId && `${optionTestId}-id-input`}
                  />
                </QuestionOptionRow>
              </SortableEditorListItem>
            )
          })}
        </EditorList>
      </FormGroupContainer>
    </>
  )
})

interface LabelContainerProps extends EditorContainerProps<
  | EffectQuestionEditorViewModel
  | PathQuestionEditorViewModel
  | InventoryItemQuestionEditorViewModel
  | VariableQuestionEditorViewModel
  | PersonalOrganizerCollectedValueQuestionEditorViewModel
> {
  help: string
}

const LabelContainer = observer(function LabelContainer({
  vm,
  isDisabled,
  help,
  testId,
}: LabelContainerProps) {
  return (
    <TextInputContainer
      model={vm}
      field="label"
      label="Label"
      help={help}
      isDisabled={isDisabled}
      data-test={testId && `${testId}-label-input`}
    />
  )
})

const InventoryItemQuestionContainer = observer(
  function InventoryItemQuestionContainer({
    vm,
    isDisabled,
    testId,
  }: EditorContainerProps<InventoryItemQuestionEditorViewModel>) {
    return (
      <>
        <LabelContainer
          vm={vm}
          isDisabled={isDisabled}
          testId={testId}
          help="Not required. Label your service item element"
        />
        <QuestionFieldRow
          aside={
            <FormGroupContainer
              label="Amount"
              error={vm.validation.getError('quantity')}
            >
              <NumericInput
                value={vm.quantity}
                onChange={vm.setQuantity}
                placeholder="Amount"
                isDisabled={isDisabled}
                status={
                  vm.validation.getError('quantity') ? 'danger' : 'default'
                }
                testId={testId && `${testId}-amount-input`}
              />
            </FormGroupContainer>
          }
        >
          <FormGroupContainer
            label="Service item"
            error={vm.validation.getError('inventoryItemId')}
          >
            <SingleOptionSelect
              value={vm.inventoryItemId}
              options={vm.inventoryItemElementOptions}
              onChange={vm.changeInventoryItem}
              placeholder="Choose service item"
              isDisabled={isDisabled}
              isInvalid={!!vm.validation.getError('inventoryItemId')}
              testId={testId && `${testId}-service-item-select`}
            />
          </FormGroupContainer>
        </QuestionFieldRow>
      </>
    )
  },
)

const VariableQuestionContainer = observer(function VariableQuestionContainer({
  vm,
  isDisabled,
  testId,
}: EditorContainerProps<VariableQuestionEditorViewModel>) {
  return (
    <>
      <LabelContainer
        vm={vm}
        isDisabled={isDisabled}
        testId={testId}
        help="Not required, but might help out your future self with understanding what this Variable does."
      />
      <FormGroupContainer
        label="Type"
        help="The type helps us ensure the right data gets put into this variable."
        error={vm.validation.getError('variableValueType')}
      >
        <TypeSelector
          value={vm.variableValueType}
          onChange={vm.setVariableValueType}
          isDisabled={isDisabled}
          testId={testId && `${testId}-type-select`}
        />
      </FormGroupContainer>
      <FormulaFieldContainer
        label="Initial value formula"
        help="If not specified, the variable is initialized to the selected type's default value."
        vm={vm.expr}
        isDisabled={isDisabled}
        testId={testId && `${testId}-formula`}
      />
    </>
  )
})

const CollectedValueQuestionContainer = observer(
  function CollectedValueQuestionContainer({
    vm,
    isDisabled,
    testId,
  }: EditorContainerProps<PersonalOrganizerCollectedValueQuestionEditorViewModel>) {
    return (
      <>
        <LabelContainer
          vm={vm}
          isDisabled={isDisabled}
          testId={testId}
          help="Not required, but might help out your future self with understanding what this value is used for."
        />
        <FormGroupContainer
          label="Binding"
          help="The organizer value to collect. It decides the type."
          error={vm.validation.getError('collectBindingId')}
        >
          <SingleOptionSelect
            value={vm.collectBindingId}
            options={vm.bindingOptions}
            onChange={vm.setBindingId}
            placeholder="Choose a binding"
            isDisabled={isDisabled}
            isInvalid={!!vm.validation.getError('collectBindingId')}
            testId={testId && `${testId}-binding-select`}
          />
        </FormGroupContainer>
        {vm.valueType && (
          <FormGroupContainer label="Type">
            <TypeBadge type={vm.valueType} />
          </FormGroupContainer>
        )}
        <FormulaFieldContainer
          label="Fallback value formula"
          help="Used when the organizer has no value. If not specified, the value is the type's default value."
          vm={vm.expr}
          isDisabled={isDisabled}
          testId={testId && `${testId}-formula`}
        />
      </>
    )
  },
)

const PrefillQuestionContainer = observer(function PrefillQuestionContainer({
  vm,
  isDisabled,
  testId,
}: EditorContainerProps<PersonalOrganizerPrefillQuestionEditorViewModel>) {
  return (
    <>
      <FormGroupContainer
        label="Binding"
        help="The organizer field to prefill. It decides the type."
        error={vm.validation.getError('prefillBindingId')}
      >
        <SingleOptionSelect
          value={vm.prefillBindingId}
          options={vm.bindingOptions}
          onChange={vm.setBindingId}
          placeholder="Choose a binding"
          isDisabled={isDisabled}
          isInvalid={!!vm.validation.getError('prefillBindingId')}
          testId={testId && `${testId}-binding-select`}
        />
      </FormGroupContainer>
      {vm.bindingValueType && (
        <FormGroupContainer label="Type">
          <TypeBadge type={vm.bindingValueType} />
        </FormGroupContainer>
      )}
      <FormulaFieldContainer
        label="Value formula"
        help="The value to prefill the organizer field with."
        vm={vm.expr}
        isDisabled={isDisabled}
        testId={testId && `${testId}-formula`}
      />
    </>
  )
})
